import { useCallback, useEffect, useRef } from 'react'
import { classifyError } from './classifyError'
import { getErrorMessage, getErrorSignature } from './errorHelpers'
import { trackAction } from './events'
import type { LendBorrowActionType, MarketType } from './types'

interface UseTrackedLendBorrowActionParams {
  actionType: LendBorrowActionType
  token: string
  market: string
  marketType: MarketType
  amount: string
  txHash?: string
  isConfirmed: boolean
  error?: unknown
}

export function useTrackedLendBorrowAction({
  actionType,
  token,
  market,
  marketType,
  amount,
  txHash,
  isConfirmed,
  error,
}: UseTrackedLendBorrowActionParams): { markSubmitted: () => void } {
  const activeAttemptRef = useRef(0)
  const finalizedAttemptRef = useRef(0)
  const submitTimeRef = useRef(0)
  const lastSignedTxHashRef = useRef<string | undefined>(undefined)
  const lastErrorSignatureRef = useRef('')

  const markSubmitted = useCallback(() => {
    activeAttemptRef.current += 1
    finalizedAttemptRef.current = 0
    submitTimeRef.current = Date.now()
    lastSignedTxHashRef.current = undefined
    lastErrorSignatureRef.current = getErrorSignature(error)

    trackAction('submitted', {
      action_type: actionType,
      token,
      market,
      market_type: marketType,
      amount,
    })
  }, [actionType, amount, error, market, marketType, token])

  useEffect(() => {
    if (!txHash || activeAttemptRef.current === 0 || lastSignedTxHashRef.current === txHash) {
      return
    }

    lastSignedTxHashRef.current = txHash

    trackAction('signed', {
      action_type: actionType,
      token,
      market,
      market_type: marketType,
      amount,
      tx_hash: txHash,
    })
  }, [actionType, amount, market, marketType, token, txHash])

  useEffect(() => {
    if (
      !isConfirmed ||
      !txHash ||
      activeAttemptRef.current === 0 ||
      finalizedAttemptRef.current === activeAttemptRef.current
    ) {
      return
    }

    finalizedAttemptRef.current = activeAttemptRef.current

    trackAction('completed', {
      action_type: actionType,
      token,
      market,
      market_type: marketType,
      amount,
      tx_hash: txHash,
      duration_ms: submitTimeRef.current > 0 ? Date.now() - submitTimeRef.current : 0,
    })
  }, [actionType, amount, isConfirmed, market, marketType, token, txHash])

  useEffect(() => {
    const errorSignature = getErrorSignature(error)
    if (
      !error ||
      !errorSignature ||
      activeAttemptRef.current === 0 ||
      finalizedAttemptRef.current === activeAttemptRef.current ||
      errorSignature === lastErrorSignatureRef.current
    ) {
      return
    }

    lastErrorSignatureRef.current = errorSignature
    finalizedAttemptRef.current = activeAttemptRef.current

    trackAction('failed', {
      action_type: actionType,
      token,
      market,
      market_type: marketType,
      amount,
      error_type: classifyError(error),
      error_message: getErrorMessage(error),
      failed_at: txHash || lastSignedTxHashRef.current ? 'confirmation' : 'sign',
    })
  }, [actionType, amount, error, market, marketType, token, txHash])

  return { markSubmitted }
}
